import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./RegisterOssAccessCodePage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const ACCESS_STORAGE_KEY = "legup_oss_access_context";
const DEFAULT_FORM = { email: "", access_code: "" };

function isValidEmail(value = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function getApiMessage(data, fallback) {
  const candidates = [
    data?.access_code,
    data?.email,
    data?.detail,
    data?.non_field_errors,
    data?.message,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length)
      return candidate.map(String).join(" ");
    if (typeof candidate === "string" && candidate.trim()) return candidate;
  }
  return fallback;
}

export default function RegisterOssAccessCodePage() {
  const navigate = useNavigate();
  const { t } = useTranslation("oss-registration-access-code");
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [ok, setOk] = React.useState("");
  const navigateTimer = React.useRef(null);

  React.useEffect(
    () => () => {
      if (navigateTimer.current) window.clearTimeout(navigateTimer.current);
    },
    [],
  );

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "email" ? value.toLowerCase() : value.toUpperCase(),
    }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setErr("");
    setOk("");
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setOk("");
    const errors = {};
    if (!form.email.trim()) errors.email = t("validation.emailRequired");
    else if (!isValidEmail(form.email))
      errors.email = t("validation.emailInvalid");
    if (!form.access_code.trim())
      errors.access_code = t("validation.accessCodeRequired");
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setErr(t("messages.correctHighlighted"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/oss/access-code/preview/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          access_code: form.access_code.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = getApiMessage(data, t("messages.invalidAccessCode"));
        setErr(message);
        if (data?.access_code)
          setFieldErrors((prev) => ({ ...prev, access_code: message }));
        return;
      }
      localStorage.setItem(
        ACCESS_STORAGE_KEY,
        JSON.stringify({
          email: form.email.trim().toLowerCase(),
          access_code: form.access_code.trim(),
          preview: data,
        }),
      );
      setOk(t("messages.verified"));
      navigateTimer.current = window.setTimeout(() => {
        navigate("/register/oss/company-details");
      }, 700);
    } catch (ex) {
      setErr(ex?.message || String(ex));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="oss-access-page">
      <section className="oss-access-hero">
        <p className="oss-access-kicker">{t("hero.kicker")}</p>
        <h1>{t("hero.title")}</h1>
        <p>{t("hero.subtitle")}</p>
      </section>
      <form className="oss-access-card" onSubmit={submit}>
        <div className="oss-access-card-header">
          <h2>{t("card.title")}</h2>
          <p>{t("card.subtitle")}</p>
        </div>
        <div className="oss-access-grid">
          <div className="oss-access-field">
            <label htmlFor="oss-registration-access-email">
              {t("fields.email")} <span className="oss-required">*</span>
            </label>
            <input
              id="oss-registration-access-email"
              name="email"
              type="email"
              placeholder={t("placeholders.email")}
              value={form.email}
              onChange={onChange}
              className={fieldErrors.email ? "oss-access-input-error" : ""}
            />
            {fieldErrors.email && <span>{fieldErrors.email}</span>}
          </div>
          <div className="oss-access-field">
            <label htmlFor="oss-registration-access-code">
              {t("fields.accessCode")} <span className="oss-required">*</span>
            </label>
            <input
              id="oss-registration-access-code"
              name="access_code"
              placeholder={t("placeholders.accessCode")}
              value={form.access_code}
              onChange={onChange}
              className={
                fieldErrors.access_code ? "oss-access-input-error" : ""
              }
            />
            {fieldErrors.access_code && <span>{fieldErrors.access_code}</span>}
          </div>
        </div>
        {(err || ok) && (
          <div
            className="oss-access-message-area"
            role={err ? "alert" : "status"}
          >
            {err && (
              <div
                className="oss-access-message error"
                style={{
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  maxHeight: "none",
                  height: "auto",
                  overflow: "visible",
                }}
              >
                {err}
              </div>
            )}
            {ok && <div className="oss-access-message success">{ok}</div>}
          </div>
        )}
        <div className="oss-access-footer">
          <button type="submit" disabled={loading}>
            {loading ? t("buttons.checking") : t("buttons.verify")}
          </button>
        </div>
      </form>
    </div>
  );
}
